import * as React from "react";

import { styled } from "@mui/system";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import CustomMenuItem from "widgets/MenuItem/CustomMenuItem";
import { Select } from "widgets";
import Chip from "@mui/material/Chip";
import { idsMatch } from "helpers/schoolUtils";
import { ZERO , ONE} from "constants/constants";

const StyledFormControl = styled(FormControl)({
  display: "flex",
  width: "100%",
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {},
    "& .MuiSelect-select": {
      padding: "10px",
      backgroundColor: "#ffffff",
      color: "black",
    },
  },
});

const StyledInputLabel = styled(InputLabel)({
  backgroundColor: "white",
  fontWeight: 400,
  padding: "0 5px",
  "&.Mui-focused": {
    color: "black",
  },
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const SELECT_ALL_VALUE = "__select_all__";

const hasValue = (value, itemValue) =>
  value.some((val) => idsMatch(val, itemValue));

const handleMenuItemClick = (value, menuItem, handleChange) => {
  if (menuItem.disabled) {
    return;
  }

  const itemValue = menuItem.value;
  const isSelected = value.some((val) => idsMatch(val, itemValue));
  const newValue = isSelected
    ? value.filter((val) => !idsMatch(val, itemValue))
    : [...value, itemValue];
  handleChange({ target: { value: newValue } });
};

const handleSelectAllClick = (value, menuItems, handleChange) => {
  const enabledValues = menuItems
    .filter((menuItem) => !menuItem.disabled)
    .map((menuItem) => menuItem.value);
  const allEnabledSelected =
    enabledValues.length > ZERO &&
    enabledValues.every((itemValue) => hasValue(value, itemValue));

  const newValue = allEnabledSelected
    ? value.filter(
        (selectedValue) =>
          !enabledValues.some((itemValue) => idsMatch(selectedValue, itemValue))
      )
    : [
        ...value,
        ...enabledValues.filter((itemValue) => !hasValue(value, itemValue)),
      ];

  handleChange({ target: { value: newValue } });
};

const MultipleSelect = (props) => {
  const {
    id,
    name,
    value,
    menuItems,
    tag,
    handleChange,
    onBlur,
    maxVisibleChips = 3,
    showSelectAllOption = false,
    selectAllLabel = "Select all",
    disabled = false,
  } = props;

  const maxVisible = Math.min(Math.max(ONE, maxVisibleChips), 99);
  const selectedValue = Array.isArray(value) ? value : [];
  const enabledMenuItems = menuItems.filter((menuItem) => !menuItem.disabled);
  const selectedEnabledCount = enabledMenuItems.filter((menuItem) =>
    hasValue(selectedValue, menuItem.value)
  ).length;
  const isAllSelected =
    enabledMenuItems.length > ZERO && selectedEnabledCount === enabledMenuItems.length;
  const isSomeSelected = selectedEnabledCount > ZERO && !isAllSelected;

  const handleSelectChange = (event) => {
    const nextValue = Array.isArray(event.target.value) ? event.target.value : [];
    if (showSelectAllOption && nextValue.includes(SELECT_ALL_VALUE)) {
      handleSelectAllClick(selectedValue, menuItems, handleChange);
      return;
    }
    handleChange(event);
  };

  const renderChips = (ids) => {
    const visible = ids.slice(ZERO, maxVisible);
    const restCount = ids.length - visible.length;
    const restIds = ids.slice(maxVisible);
    const restLabels = restIds
      .map(
        (val) =>
          menuItems.find((item) => idsMatch(item.value, val))?.label ||
          String(val)
      )
      .join(", ");

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          gap: 6,
          alignItems: "center",
          overflow: "hidden",
          minHeight: 26,
        }}
      >
        {visible.map((val) => {
          const label =
            menuItems.find((item) => idsMatch(item.value, val))?.label || val;
          return (
            <Chip
              key={val}
              label={label}
              size="small"
              sx={{
                maxWidth: 140,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          );
        })}
        {restCount > ZERO ? (
          <Chip
            key="__overflow"
            label={`+${restCount}`}
            size="small"
            variant="outlined"
            title={restLabels || `${restCount} more selected`}
            aria-label={`${restCount} additional items selected: ${restLabels}`}
            sx={{
              flexShrink: 0,
              fontWeight: 600,
              borderColor: "rgba(30, 63, 102, 0.35)",
              color: "#1e3f66",
              backgroundColor: "rgba(30, 63, 102, 0.06)",
            }}
          />
        ) : null}
      </div>
    );
  };

  return (
    <StyledFormControl variant="outlined">
      <StyledInputLabel id={id}>{tag}</StyledInputLabel>
      <Select
        id={id}
        name={name}
        multiple
        value={selectedValue}
        onChange={handleSelectChange}
        onBlur={onBlur}
        disabled={disabled}
        displayEmpty
        renderValue={(selected) =>
          renderChips(Array.isArray(selected) ? selected : [])
        }
        MenuProps={MenuProps}
      >
        {showSelectAllOption ? (
          <CustomMenuItem
            key={SELECT_ALL_VALUE}
            value={SELECT_ALL_VALUE}
            disabled={disabled || enabledMenuItems.length === ZERO}
          >
            <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} />
            {selectAllLabel}
          </CustomMenuItem>
        ) : null}
        {menuItems.map((menuItem) => (
          <CustomMenuItem
            key={menuItem.key}
            value={menuItem.value}
            onClick={() =>
              handleMenuItemClick(
                selectedValue,
                menuItem,
                handleChange
              )
            }
            disabled={menuItem.disabled}
          >
            <Checkbox
              checked={selectedValue.some((selected) =>
                idsMatch(selected, menuItem.value)
              )}
              disabled={menuItem.disabled}
            />
            {menuItem.label}
          </CustomMenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};

export default MultipleSelect;
